import { useTemplateReviewState } from '@/features/onboarding-v2/hooks/useTemplateReviewState';
import { TemplateAssemblyLoader } from '@/features/templateBuilder/components/loadingAnimations/templateAssemblyLoader';
import {
  SegmentedControl,
  SegmentedControlContent,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import { TypographyCaption, TypographyH3 } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { Check, NotebookPen, Pencil, Table } from 'lucide-react';

import { TemplateEditorTab } from './TemplateEditorTab';

interface TemplateReviewContentProps {
  templateName?: string;
  templateContent?: string;
  specialtyName?: string;
  isEditable?: boolean;
  isApplyingChanges?: boolean;
  onContentChange?: (content: string) => void;
  onTemplateNameChange?: (name: string) => void;
}

export function TemplateReviewContent({
  templateName,
  templateContent,
  specialtyName,
  isEditable = false,
  isApplyingChanges = false,
  onContentChange,
  onTemplateNameChange,
}: TemplateReviewContentProps) {
  const { t } = useTranslations();

  const {
    activeTab,
    setActiveTab,
    localTemplateName,
    titleInputRef,
    handleTitleChange,
    localExampleContent,
    setLocalExampleContent,
    localStructureContent,
    handleStructureChange,
    showSaved,
    handleSaved,
    isGeneratingExampleNote,
  } = useTemplateReviewState({
    templateName,
    templateContent,
    specialtyName,
    onContentChange,
    onTemplateNameChange,
  });

  return (
    <div className="flex size-full items-center justify-center overflow-hidden rounded-lg px-3 pb-6 pt-10">
      {/* Container for tooltip + card positioning */}
      <div className="relative">
        {/* Edit tooltip - shows when editing is enabled, positioned absolutely */}
        {isEditable && (
          <div className="absolute -top-10 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-lg bg-background-primary px-4 py-2 shadow-lg">
            <Pencil className="text-text-primary-invert" size={12} />
            <TypographyCaption className="text-text-primary-invert">
              {t('signup.makeChangesDirectly')}
            </TypographyCaption>
            {/* Tooltip arrow pointing down */}
            <div className="absolute -bottom-2 left-1/2 size-0 -translate-x-1/2 border-x-8 border-t-8 border-x-transparent border-t-background-primary" />
          </div>
        )}

        {/* Template preview card */}
        <div className="relative h-[700px] w-150 overflow-hidden rounded-xl border border-border bg-surface shadow-lg">
          {/* Header with template name - editable when isEditable is true */}
          <div className="px-4 pt-4">
            {isEditable ? (
              <input
                ref={titleInputRef}
                type="text"
                value={localTemplateName}
                onChange={handleTitleChange}
                placeholder={t('signup.previewDefaultTemplateName')}
                className="w-full border-none bg-transparent font-exposure text-2xl font-medium leading-tight tracking-tight text-text-primary outline-none placeholder:text-text-tertiary focus:ring-0"
              />
            ) : (
              <TypographyH3>
                {templateName || t('signup.previewDefaultTemplateName')}
              </TypographyH3>
            )}
          </div>

          <div className="px-4 pt-4">
            <SegmentedControl
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as 'example' | 'structure')}
              className="gap-4"
            >
              <SegmentedControlList>
                <SegmentedControlTrigger value="example">
                  <NotebookPen />
                  <TypographyCaption>{t('signup.previewExample')}</TypographyCaption>
                </SegmentedControlTrigger>
                <SegmentedControlTrigger value="structure">
                  <Table />
                  <TypographyCaption>{t('signup.previewStructure')}</TypographyCaption>
                </SegmentedControlTrigger>
              </SegmentedControlList>

              <SegmentedControlContent
                value="example"
                forceMount
                className="h-[540px] overflow-y-auto pl-4 pr-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {isGeneratingExampleNote ? (
                  <div className="flex h-full items-center justify-center">
                    <TemplateAssemblyLoader />
                  </div>
                ) : (
                  <TemplateEditorTab
                    content={localExampleContent}
                    onContentChange={setLocalExampleContent}
                    tabType="example"
                    placeholder={t('templateCreationRecord.examplePlaceholder')}
                    isEditable={isEditable}
                    onSaved={handleSaved}
                  />
                )}
              </SegmentedControlContent>

              <SegmentedControlContent
                value="structure"
                forceMount
                className="h-[540px] overflow-y-auto pl-4 pr-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                <TemplateEditorTab
                  content={localStructureContent}
                  onContentChange={handleStructureChange}
                  tabType="structure"
                  placeholder={t('templateCreationRecord.structurePlaceholder')}
                  isEditable={isEditable}
                  onSaved={handleSaved}
                />
              </SegmentedControlContent>
            </SegmentedControl>
          </div>

          {/* Bottom gradient fade - 111px per Figma */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 rounded-b-xl bg-gradient-to-t from-surface to-transparent" />

          {/* Loading overlay - fills the white card when applying changes */}
          {isApplyingChanges && (
            <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-surface">
              <TemplateAssemblyLoader />
            </div>
          )}

          {/* Saved indicator - positioned in bottom-right of card */}
          {showSaved && !isApplyingChanges && (
            <div className="absolute bottom-4 right-4 z-10 flex items-center gap-1 rounded-md bg-validation-success-muted px-2 py-1 text-validation-success duration-200 animate-in fade-in">
              <Check size={12} />
              <span className="text-xs font-medium">{t('common.saved')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
